import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DateTime } from 'luxon';
import { Subject } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';

import {
  CurrentUser,
  TranslationLanguage,
  TranslationMediaTypeEnum,
} from '../../../../../../graphql/service/graphql-auto-main-service';
import { TranslationKey, TranslationState } from '../../../../types/translation.type';
import { LotManualTranslationTabSubmitPayloadType } from './types/lot-manual-translation-tab-submit-payload.type';

@Component({
  selector: 'app-lot-manual-translation-tab',
  templateUrl: './lot-manual-translation-tab.component.html',
  styleUrls: ['./lot-manual-translation-tab.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LotManualTranslationTabComponent implements OnInit, OnDestroy {
  @Input() currentUser: CurrentUser;
  @Input() translationState: TranslationState;
  @Input() disabled = false;

  @Output() manualTabDataPayloadChange =
    new EventEmitter<LotManualTranslationTabSubmitPayloadType | null>();

  form: FormGroup;

  isSaturday = DateTime.now().weekday === 6;

  translationLanguages = Object.values(TranslationLanguage);
  translationMediaTypes = Object.values(TranslationMediaTypeEnum);

  private readonly mediaOrder: TranslationMediaTypeEnum[] = [
    TranslationMediaTypeEnum.Text,
    TranslationMediaTypeEnum.Audio,
  ];

  private readonly destroy$ = new Subject<void>();

  constructor(private fb: FormBuilder) {
    this.form = this.fb.group({
      language: this.fb.control(null, Validators.required),
      translationMediaType: this.fb.control(null, Validators.required),
    });
  }

  ngOnInit(): void {
    this.form.patchValue({
      language: this.currentUser?.preferredLang ?? null,
      translationMediaType: this.currentUser?.defaultTranslationMediaType ?? null,
    });

    this.form
      .get('language')!
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        this.normalizeMediaType();
        this.emitFooterState();
      });

    this.form
      .get('translationMediaType')!
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => this.emitFooterState());

    this.normalizeMediaType();
    this.emitFooterState();
  }

  private emitFooterState(): void {
    if (this.isTabDataInvalid) {
      this.manualTabDataPayloadChange.emit(null);
      return;
    }

    this.manualTabDataPayloadChange.emit(
      this.form.getRawValue() as LotManualTranslationTabSubmitPayloadType,
    );
  }

  get isTabDataInvalid(): boolean {
    if (this.disabled) {
      return true;
    }

    const lang = this.form.get('language')!.value as TranslationLanguage | null;
    const media = this.form.get('translationMediaType')!.value as TranslationMediaTypeEnum | null;

    if (!lang || !media) {
      return true;
    }

    if (this.form.invalid) {
      return true;
    }

    return this.isRequestDisabled(lang, media);
  }

  private normalizeMediaType(): void {
    const lang = this.form.get('language')!.value as TranslationLanguage | null;

    if (!lang) {
      return;
    }

    if (lang === TranslationLanguage.En) {
      this.form.patchValue(
        { translationMediaType: TranslationMediaTypeEnum.Text },
        { emitEvent: false },
      );
      return;
    }

    const current = this.form.get('translationMediaType')!.value as TranslationMediaTypeEnum | null;

    if (current && !this.isRequestDisabled(lang, current)) {
      return;
    }

    const next = this.pickFirstAvailableMediaType(lang);
    this.form.patchValue({ translationMediaType: next ?? null }, { emitEvent: false });
  }

  private pickFirstAvailableMediaType(lang: TranslationLanguage): TranslationMediaTypeEnum | null {
    for (const translationMediaType of this.mediaOrder) {
      if (!this.isRequestDisabled(lang, translationMediaType)) {
        return translationMediaType;
      }
    }

    return null;
  }

  private keyOf(lang: TranslationLanguage, media: TranslationMediaTypeEnum): TranslationKey | null {
    if (lang === TranslationLanguage.Ru && media === TranslationMediaTypeEnum.Text) {
      return 'ruText';
    }

    if (lang === TranslationLanguage.Ru && media === TranslationMediaTypeEnum.Audio) {
      return 'ruAudio';
    }

    if (lang === TranslationLanguage.En && media === TranslationMediaTypeEnum.Text) {
      return 'enText';
    }

    if (lang === TranslationLanguage.En && media === TranslationMediaTypeEnum.Audio) {
      return 'enAudio';
    }

    return null;
  }

  isRequestDisabled(lang: TranslationLanguage | null, media: TranslationMediaTypeEnum): boolean {
    if (!lang) {
      return false;
    }

    if (lang === TranslationLanguage.En && media === TranslationMediaTypeEnum.Audio) {
      return true;
    }

    const key = this.keyOf(lang, media);
    return key ? !!this.translationState?.has?.[key] : false;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
